<div class="overflow-hidden" [class.pb-24]="showStickyCta && !hideStickyNearFooter">
  <!-- ═══════════════════════ Hero ═══════════════════════ -->
  <section id="top" class="thi-campaign-hero relative pt-28 pb-16 md:pt-36 md:pb-28">
    <div class="thi-campaign-hero__glow" aria-hidden="true"></div>
    <div class="container mx-auto px-4 relative z-10">
      <div class="flex flex-col lg:grid lg:grid-cols-12 lg:gap-10 lg:items-center">
        <!-- Copy -->
        <div class="order-1 lg:col-span-5 text-center lg:text-start">
          <p class="inline-flex items-center gap-2 text-sm font-bold text-primary mb-5 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/15">
            <i class="fa fa-bolt text-xs" aria-hidden="true"></i>
            {{ c.hero.label }}
          </p>
          <h1 class="text-4xl sm:text-5xl lg:text-[3.4rem] font-bold text-foreground leading-[1.15] mb-6">
            {{ c.hero.titleBefore }}<span class="text-primary">{{ c.hero.titleHighlight }}</span>
          </h1>
          <p class="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
            {{ c.hero.subtitle }}
          </p>

          <div class="flex flex-col sm:flex-row sm:flex-wrap gap-3 justify-center lg:justify-start mb-8">
            <button
              type="button"
              (click)="scrollToPricing('hero')"
              class="thi-btn-primary-xl w-full sm:w-auto justify-center inline-flex group"
            >
              {{ c.hero.ctaPrimary }}
              <i class="transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" [ngClass]="arrowIcon" aria-hidden="true"></i>
            </button>
            <button
              *ngIf="demoVideoUrl"
              type="button"
              (click)="onWatchDemo('hero')"
              class="thi-btn-outline-xl w-full sm:w-auto justify-center inline-flex"
            >
              <i class="fa fa-play-circle" aria-hidden="true"></i>
              {{ c.hero.ctaSecondary }}
            </button>
            <a
              *ngIf="!demoVideoUrl"
              routerLink="/support"
              class="thi-btn-outline-xl w-full sm:w-auto justify-center inline-flex"
            >
              <i class="fa fa-play-circle" aria-hidden="true"></i>
              {{ c.hero.ctaSecondary }}
            </a>
          </div>

          <ul class="flex flex-wrap items-center gap-x-6 gap-y-3 justify-center lg:justify-start text-sm font-medium text-muted-foreground">
            <li class="flex items-center gap-2">
              <span class="thi-trust-dot"><i class="fa fa-credit-card" aria-hidden="true"></i></span>
              {{ c.hero.trustNoCard }}
            </li>
            <li class="flex items-center gap-2">
              <span class="thi-trust-dot"><i class="fa fa-rocket" aria-hidden="true"></i></span>
              {{ c.hero.trustQuick }}
            </li>
            <li class="flex items-center gap-2">
              <span class="thi-trust-dot"><i class="fa fa-globe" aria-hidden="true"></i></span>
              {{ c.hero.trustLang }}
            </li>
          </ul>
        </div>

        <!-- Visual composite -->
        <div class="order-2 lg:col-span-7 mt-14 lg:mt-0">
          <div class="thi-hero-stage">
            <div class="thi-hero-laptop">
              <img
                src="assets/landing-thi/dashboard-mockup-480w.png"
                srcset="assets/landing-thi/dashboard-mockup-480w.png 480w, assets/landing-thi/dashboard-mockup-960w.png 960w"
                sizes="(max-width: 1024px) 92vw, 56vw"
                width="960"
                height="540"
                fetchpriority="high"
                decoding="async"
                alt=""
              />
            </div>

            <div class="thi-hero-phone">
              <img
                src="assets/landing-thi/ai-receipt-scan-400w.png"
                width="400"
                height="400"
                loading="lazy"
                decoding="async"
                alt=""
              />
              <div class="thi-hero-phone__badge">
                <i class="fa fa-check" aria-hidden="true"></i>
                <span>{{ c.hero.extractedTitle }}</span>
              </div>
            </div>

            <div class="thi-hero-card thi-hero-card--advisor">
              <div class="thi-hero-card__head">
                <span class="thi-hero-card__icon"><i class="fa fa-magic" aria-hidden="true"></i></span>
                <span class="thi-hero-card__title">{{ c.hero.aiAdvisorTitle }}</span>
              </div>
              <p class="thi-hero-card__text">{{ c.hero.aiAdvisorInsight }}</p>
            </div>

            <div class="thi-hero-card thi-hero-card--extract">
              <div class="thi-hero-card__head">
                <span class="thi-hero-card__icon thi-hero-card__icon--success"><i class="fa fa-check" aria-hidden="true"></i></span>
                <span class="thi-hero-card__title">{{ c.hero.extractedTitle }}</span>
              </div>
              <div class="thi-hero-card__row">
                <span>{{ c.aiExpense.amount }}</span>
                <strong dir="ltr">{{ c.aiExpense.sampleAmount }}</strong>
              </div>
              <div class="thi-hero-card__row">
                <span>{{ c.aiExpense.date }}</span>
                <strong dir="ltr">{{ c.aiExpense.sampleDate }}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════ Features ═══════════════════════ -->
  <section id="features" class="thi-anchor-offset py-20 md:py-28 bg-background">
    <div class="container mx-auto px-4">
      <div class="max-w-2xl mx-auto text-center mb-14">
        <h2 class="text-3xl md:text-4xl font-bold text-foreground">{{ c.features.title }}</h2>
      </div>
      <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 max-w-6xl mx-auto">
        <div
          *ngFor="let f of featureCards"
          class="thi-feature-card group bg-card border border-border rounded-2xl p-7 text-center"
        >
          <div class="thi-icon-ring mx-auto mb-5">
            <i [ngClass]="f.icon" aria-hidden="true"></i>
          </div>
          <h3 class="text-lg font-bold text-foreground mb-2.5">{{ f.title }}</h3>
          <p class="text-sm text-muted-foreground leading-relaxed">{{ f.desc }}</p>
        </div>
      </div>
      <div class="text-center mt-12">
        <button
          type="button"
          (click)="scrollToPricing('features')"
          class="thi-btn-primary-lg inline-flex group"
        >
          {{ c.hero.ctaPrimary }}
          <i class="transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" [ngClass]="arrowIcon" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════ AI Expense ═══════════════════════ -->
  <section class="py-20 md:py-28 thi-section-gradient-soft">
    <div class="container mx-auto px-4 relative z-10">
      <div class="max-w-3xl mx-auto text-center mb-14">
        <p class="inline-flex items-center gap-2 text-sm font-bold text-primary mb-4 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/15">
          <i class="fa fa-camera" aria-hidden="true"></i>
          AI
        </p>
        <h2 class="text-3xl md:text-4xl font-bold text-foreground mb-4">{{ c.aiExpense.title }}</h2>
        <p class="text-muted-foreground text-lg leading-relaxed">{{ c.aiExpense.text }}</p>
      </div>

      <div class="grid lg:grid-cols-2 gap-14 items-center max-w-6xl mx-auto">
        <!-- Steps + benefits -->
        <div class="order-2 lg:order-1">
          <ol class="thi-expense-steps">
            <li *ngFor="let step of c.aiExpense.steps; let i = index" class="thi-expense-steps__item">
              <span class="thi-expense-steps__num">{{ i + 1 }}</span>
              <span class="thi-expense-steps__label">{{ step }}</span>
            </li>
          </ol>

          <ul class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mt-10">
            <li *ngFor="let b of c.aiExpense.benefits" class="flex items-start gap-2.5 text-sm text-foreground/80">
              <span class="thi-check-dot"><i class="fa fa-check" aria-hidden="true"></i></span>
              {{ b }}
            </li>
          </ul>
        </div>

        <!-- Phone + extraction card -->
        <div class="order-1 lg:order-2">
          <div class="thi-extract-stage">
            <div class="thi-extract-phone">
              <img
                src="assets/landing-thi/ai-receipt-scan-400w.png"
                width="400"
                height="400"
                loading="lazy"
                decoding="async"
                alt=""
              />
            </div>
            <div class="thi-extract-card">
              <div class="flex items-center gap-2.5 mb-5">
                <span class="thi-icon-chip thi-icon-chip--success"><i class="fa fa-check" aria-hidden="true"></i></span>
                <h3 class="font-bold text-foreground text-base m-0">{{ c.aiExpense.cardTitle }}</h3>
              </div>
              <div class="thi-extract-card__row">
                <span class="thi-icon-chip"><i class="fa fa-money" aria-hidden="true"></i></span>
                <span class="thi-extract-card__label">{{ c.aiExpense.amount }}</span>
                <strong class="thi-extract-card__value" dir="ltr">{{ c.aiExpense.sampleAmount }}</strong>
              </div>
              <div class="thi-extract-card__row">
                <span class="thi-icon-chip"><i class="fa fa-calendar" aria-hidden="true"></i></span>
                <span class="thi-extract-card__label">{{ c.aiExpense.date }}</span>
                <strong class="thi-extract-card__value" dir="ltr">{{ c.aiExpense.sampleDate }}</strong>
              </div>
              <div class="thi-extract-card__row">
                <span class="thi-icon-chip"><i class="fa fa-tag" aria-hidden="true"></i></span>
                <span class="thi-extract-card__label">{{ c.aiExpense.category }}</span>
                <strong class="thi-extract-card__value">{{ c.aiExpense.sampleCategory }}</strong>
              </div>
              <div class="thi-extract-card__row">
                <span class="thi-icon-chip"><i class="fa fa-shopping-bag" aria-hidden="true"></i></span>
                <span class="thi-extract-card__label">{{ c.aiExpense.merchant }}</span>
                <strong class="thi-extract-card__value">{{ c.aiExpense.sampleMerchant }}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="text-center mt-14">
        <button
          type="button"
          (click)="scrollToPricing('ai_expense')"
          class="thi-btn-primary-lg inline-flex group"
        >
          {{ c.hero.ctaPrimary }}
          <i class="transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" [ngClass]="arrowIcon" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════ AI Advisor ═══════════════════════ -->
  <section class="py-20 md:py-28 bg-background">
    <div class="container mx-auto px-4">
      <div class="grid lg:grid-cols-2 gap-14 items-center max-w-6xl mx-auto">
        <!-- Copy -->
        <div class="text-center lg:text-start">
          <p class="inline-flex items-center gap-2 text-sm font-bold text-primary mb-4 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/15">
            <i class="fa fa-magic" aria-hidden="true"></i>
            {{ c.aiAdvisor.label }}
          </p>
          <h2 class="text-3xl md:text-4xl font-bold text-foreground mb-5">{{ c.aiAdvisor.title }}</h2>
          <p class="text-muted-foreground text-lg leading-relaxed mb-8">{{ c.aiAdvisor.text }}</p>
          <ul class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-start">
            <li *ngFor="let b of c.aiAdvisor.benefits" class="flex items-start gap-2.5 text-sm text-foreground/80">
              <span class="thi-check-dot thi-check-dot--success"><i class="fa fa-check" aria-hidden="true"></i></span>
              {{ b }}
            </li>
          </ul>
        </div>

        <!-- Advisor card + chart -->
        <div class="thi-advisor-stage">
          <div class="thi-advisor-card">
            <div class="flex items-center gap-3 mb-2">
              <span class="thi-icon-chip thi-icon-chip--primary"><i class="fa fa-magic" aria-hidden="true"></i></span>
              <h3 class="font-bold text-foreground text-base m-0">{{ c.aiAdvisor.cardTitle }}</h3>
            </div>
            <p class="text-sm text-muted-foreground mb-4">{{ c.aiAdvisor.cardIntro }}</p>
            <ul class="space-y-2.5 mb-5 list-none p-0 m-0">
              <li
                *ngFor="let ins of c.aiAdvisor.insights"
                class="thi-advisor-insight"
              >
                <i class="fa fa-line-chart" aria-hidden="true"></i>
                {{ ins }}
              </li>
            </ul>
            <div class="thi-advisor-input">
              <span class="thi-advisor-input__placeholder">{{ c.aiAdvisor.inputPlaceholder }}</span>
              <span class="thi-advisor-input__send">
                <i class="fa fa-paper-plane" aria-hidden="true"></i>
              </span>
            </div>
          </div>

          <div class="thi-chart-card" aria-hidden="true">
            <div class="flex items-center justify-between mb-3">
              <span class="text-xs font-bold text-foreground">{{ c.reports.metrics[0] }}</span>
              <span class="thi-chart-card__badge">+32%</span>
            </div>
            <div class="thi-chart-bars">
              <span style="height: 34%"></span>
              <span style="height: 52%"></span>
              <span style="height: 44%"></span>
              <span style="height: 68%"></span>
              <span class="is-active" style="height: 88%"></span>
            </div>
          </div>
        </div>
      </div>
      <div class="text-center mt-14">
        <button
          type="button"
          (click)="scrollToPricing('ai_advisor')"
          class="thi-btn-primary-lg inline-flex group"
        >
          {{ c.hero.ctaPrimary }}
          <i class="transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" [ngClass]="arrowIcon" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════ Reports ═══════════════════════ -->
  <section class="py-20 md:py-28 thi-section-gradient-soft">
    <div class="container mx-auto px-4 max-w-5xl text-center relative z-10">
      <h2 class="text-3xl md:text-4xl font-bold text-foreground mb-4">{{ c.reports.title }}</h2>
      <p class="text-lg text-muted-foreground leading-relaxed mb-12 max-w-3xl mx-auto">{{ c.reports.text }}</p>
      <div class="grid grid-cols-2 md:grid-cols-3 gap-5 mb-10">
        <div
          *ngFor="let m of c.reports.metrics; let i = index"
          class="thi-metric-card"
        >
          <span class="thi-icon-ring thi-icon-ring--sm mx-auto mb-3">
            <i [ngClass]="metricIcons[i]" aria-hidden="true"></i>
          </span>
          <span class="text-sm font-bold text-foreground">{{ m }}</span>
        </div>
      </div>
      <p class="text-base font-semibold text-primary mb-10">{{ c.reports.supporting }}</p>
      <button
        type="button"
        (click)="scrollToPricing('reports')"
        class="thi-btn-primary-lg inline-flex group"
      >
        {{ c.hero.ctaPrimary }}
        <i class="transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" [ngClass]="arrowIcon" aria-hidden="true"></i>
      </button>
    </div>
  </section>

  <!-- ═══════════════════════ How it works ═══════════════════════ -->
  <section class="py-20 md:py-28 bg-background">
    <div class="container mx-auto px-4">
      <div class="max-w-2xl mx-auto text-center mb-16">
        <h2 class="text-3xl md:text-4xl font-bold text-foreground">{{ c.howItWorks.title }}</h2>
      </div>
      <div class="thi-steps-grid max-w-5xl mx-auto mb-14">
        <div *ngFor="let step of c.howItWorks.steps; let i = index" class="thi-step">
          <div class="thi-step__num">{{ i + 1 }}</div>
          <h3 class="font-bold text-foreground text-lg mb-2">{{ step.title }}</h3>
          <p class="text-sm text-muted-foreground leading-relaxed max-w-[16rem] mx-auto">{{ step.desc }}</p>
        </div>
      </div>
      <div class="text-center">
        <button
          type="button"
          (click)="scrollToPricing('how_it_works')"
          class="thi-btn-primary-lg inline-flex group"
        >
          {{ c.hero.ctaPrimary }}
          <i class="transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" [ngClass]="arrowIcon" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════ Pricing (shared) ═══════════════════════ -->
  <app-thi-public-pricing
    class="thi-anchor-offset"
    [lang]="lang"
    [useCampaignCopy]="true"
    [useCampaignAttribution]="true"
    [mobileComparisonTabs]="true"
    (pricingVisible)="onPricingVisible()"
    (packageSelected)="onPackageSelected($event)"
  ></app-thi-public-pricing>

  <!-- ═══════════════════════ Why THI ═══════════════════════ -->
  <section class="py-20 md:py-28 bg-background">
    <div class="container mx-auto px-4">
      <div class="max-w-2xl mx-auto text-center mb-14">
        <h2 class="text-3xl md:text-4xl font-bold text-foreground">{{ c.whyThi.title }}</h2>
      </div>
      <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
        <div *ngFor="let w of whyItems" class="thi-feature-card bg-card border border-border rounded-2xl p-7 text-center">
          <div class="thi-icon-ring mx-auto mb-5">
            <i [ngClass]="w.icon" aria-hidden="true"></i>
          </div>
          <h3 class="font-bold text-foreground mb-2.5">{{ w.title }}</h3>
          <p class="text-sm text-muted-foreground leading-relaxed">{{ w.desc }}</p>
        </div>
      </div>
      <div class="text-center mt-12">
        <button
          type="button"
          (click)="scrollToPricing('why_thi')"
          class="thi-btn-primary-lg inline-flex group"
        >
          {{ c.hero.ctaPrimary }}
          <i class="transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" [ngClass]="arrowIcon" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════ Languages ═══════════════════════ -->
  <section class="py-16 md:py-20 thi-section-gradient-soft">
    <div class="container mx-auto px-4 max-w-4xl text-center relative z-10">
      <h2 class="text-2xl md:text-3xl font-bold text-foreground mb-3">{{ c.languages.title }}</h2>
      <p class="text-muted-foreground mb-10">{{ c.languages.text }}</p>
      <div class="grid sm:grid-cols-3 gap-5">
        <div class="thi-lang-card" dir="rtl">
          <span class="thi-lang-card__tag">RTL</span>
          <p class="font-bold text-foreground text-lg mb-1">{{ c.languages.ar.title }}</p>
          <p class="text-xs text-muted-foreground m-0">{{ c.languages.ar.desc }}</p>
        </div>
        <div class="thi-lang-card" dir="ltr">
          <span class="thi-lang-card__tag">LTR</span>
          <p class="font-bold text-foreground text-lg mb-1">{{ c.languages.en.title }}</p>
          <p class="text-xs text-muted-foreground m-0">{{ c.languages.en.desc }}</p>
        </div>
        <div class="thi-lang-card" dir="rtl">
          <span class="thi-lang-card__tag">RTL</span>
          <p class="font-bold text-foreground text-lg mb-1">{{ c.languages.he.title }}</p>
          <p class="text-xs text-muted-foreground m-0">{{ c.languages.he.desc }}</p>
        </div>
      </div>
      <div class="text-center mt-12">
        <button
          type="button"
          (click)="scrollToPricing('languages')"
          class="thi-btn-primary-lg inline-flex group"
        >
          {{ c.hero.ctaPrimary }}
          <i class="transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" [ngClass]="arrowIcon" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════ FAQ ═══════════════════════ -->
  <section id="faq" class="thi-anchor-offset py-20 md:py-28 bg-background">
    <div class="container mx-auto px-4">
      <div class="max-w-2xl mx-auto text-center mb-14">
        <h2 class="text-3xl md:text-4xl font-bold text-foreground">{{ c.faq.title }}</h2>
      </div>
      <div class="max-w-3xl mx-auto space-y-3">
        <div
          *ngFor="let item of c.faq.items; let i = index"
          class="thi-faq-item"
          [class.thi-faq-item--open]="openFaqIndex === i"
        >
          <button
            type="button"
            (click)="toggleFaq(i)"
            [attr.aria-expanded]="openFaqIndex === i"
            class="thi-faq-trigger w-full text-start py-5 px-6 font-bold text-foreground flex justify-between items-center gap-4"
          >
            {{ item.q }}
            <span class="thi-faq-item__toggle" aria-hidden="true">
              <i class="fa" [class.fa-plus]="openFaqIndex !== i" [class.fa-minus]="openFaqIndex === i"></i>
            </span>
          </button>
          <div *ngIf="openFaqIndex === i" class="px-6 pb-5 text-muted-foreground text-sm leading-relaxed">
            {{ item.a }}
          </div>
        </div>
      </div>
      <div class="text-center mt-12">
        <button
          type="button"
          (click)="scrollToPricing('faq')"
          class="thi-btn-primary-lg inline-flex group"
        >
          {{ c.hero.ctaPrimary }}
          <i class="transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" [ngClass]="arrowIcon" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  </section>

  <!-- ═══════════════════════ Final CTA ═══════════════════════ -->
  <section #finalCta id="final-cta" class="thi-final-cta py-20 md:py-28">
    <div class="container mx-auto px-4 text-center max-w-3xl relative z-10">
      <h2 class="thi-final-cta__title text-3xl md:text-4xl lg:text-[2.75rem] font-bold mb-5 leading-tight">{{ c.finalCta.title }}</h2>
      <p class="thi-final-cta__text text-lg leading-relaxed mb-10">{{ c.finalCta.text }}</p>
      <a
        routerLink="/register"
        [queryParams]="registerParams"
        (click)="onStartFree('final_cta')"
        class="thi-final-cta__btn group"
      >
        {{ c.finalCta.cta }}
        <i class="transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" [ngClass]="arrowIcon" aria-hidden="true"></i>
      </a>
      <p class="thi-final-cta__trust mt-6 text-sm">{{ c.finalCta.trust }}</p>
    </div>
  </section>

  <!-- ═══════════════════════ Sticky mobile CTA ═══════════════════════ -->
  <div
    *ngIf="showStickyCta && !hideStickyNearFooter"
    class="thi-campaign-sticky-cta md:hidden fixed bottom-0 inset-x-0 z-40 p-3"
  >
    <a
      routerLink="/register"
      [queryParams]="registerParams"
      (click)="onStartFree('sticky_mobile')"
      class="thi-btn-default w-full justify-center inline-flex shadow-lg"
    >
      {{ c.stickyCta }}
    </a>
  </div>
</div>
